/**
 * PolePhotosTab - Photo gallery tab for pole documentation
 */
import React, { useState, useRef, useEffect } from 'react';
import { Pole, PolePhoto } from '../../types';
import { polesAPI } from '../../services/api';
import './PolePhotosTab.css';

interface PolePhotosTabProps {
  pole: Pole;
  projectId: number;
  onUpdate: (updatedPole: Pole) => void;
}

export const PolePhotosTab: React.FC<PolePhotosTabProps> = ({ pole, projectId, onUpdate }) => {
  const [photos, setPhotos] = useState<PolePhoto[]>(pole.photos || []);
  const [uploading, setUploading] = useState(false);
  const [editingCaption, setEditingCaption] = useState<string | null>(null);
  const [captionText, setCaptionText] = useState('');
  const [uploadCaption, setUploadCaption] = useState('');

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const isUpdatingRef = useRef(false); // Track if we're currently updating photos

  // Update photos from prop, but ONLY if:
  // 1. Pole ID changed (dialog reopened for different pole)
  // 2. We're not currently updating photos (to prevent overwriting local changes)
  useEffect(() => {
    console.log('🔄 [PolePhotosTab] useEffect triggered:', {
      poleId: pole.id,
      poleName: pole.name,
      photosFromProp: pole.photos?.length || 0,
      photoIdsFromProp: pole.photos?.map(p => p.id) || [],
      isUpdating: isUpdatingRef.current
    });

    // Don't update if we're currently modifying photos
    if (isUpdatingRef.current) {
      console.log('⏸️ [PolePhotosTab] Skipping update - currently modifying photos');
      return;
    }

    setPhotos(pole.photos || []);
  }, [pole.id, pole.photos]);

  const handleFileSelect = async (file: File, caption: string) => {
    if (!file) return;

    console.log('📸 [PolePhotosTab] handleFileSelect called:', {
      fileName: file.name,
      fileSize: file.size,
      currentPhotosCount: photos.length,
      poleId: pole.id,
      projectId
    });

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      alert('Prosím vyberte obrázok (JPEG, PNG, WebP, SVG)');
      return;
    }

    // Validate file size (20MB)
    if (file.size > 20 * 1024 * 1024) {
      alert('Súbor je príliš veľký. Maximálna veľkosť je 20MB.');
      return;
    }

    // Check photo limit
    if (photos.length >= 10) {
      alert('Maximálny počet fotiek je 10.');
      return;
    }

    setUploading(true);
    isUpdatingRef.current = true; // Prevent useEffect from overwriting our changes

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (caption) {
        formData.append('caption', caption);
      }

      console.log('📤 [PolePhotosTab] Uploading photo to API...');
      const response = await polesAPI.uploadPhoto(projectId, pole.id, formData);
      const newPhoto = response.data.photo;
      console.log('✅ [PolePhotosTab] Photo uploaded successfully:', newPhoto);

      // Update local state
      const updatedPhotos = [...photos, newPhoto];
      console.log('📸 [PolePhotosTab] Updating local state:', {
        oldCount: photos.length,
        newCount: updatedPhotos.length,
        photoIds: updatedPhotos.map(p => p.id),
        newPhotoId: newPhoto.id
      });

      // Force state update by creating a completely new array
      setPhotos([...updatedPhotos]);

      // Update parent with new pole object
      console.log('⬆️ [PolePhotosTab] Calling onUpdate with updated photos...');
      const updatedPole = { ...pole, photos: [...updatedPhotos] };
      onUpdate(updatedPole);

      // Clear caption input
      setUploadCaption('');

      alert('✅ Fotka bola úspešne nahraná!');

      // Wait a bit before allowing useEffect to update again
      setTimeout(() => {
        isUpdatingRef.current = false;
        console.log('🔓 [PolePhotosTab] Upload complete, allowing useEffect updates');
      }, 500);
    } catch (error: any) {
      console.error('❌ [PolePhotosTab] Error uploading photo:', error);
      alert(`❌ Chyba pri nahrávaní fotky: ${error.response?.data?.detail || error.message}`);
      isUpdatingRef.current = false; // Reset on error
    } finally {
      setUploading(false);
    }
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file, uploadCaption);
    }
    // Reset input
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const handleGallerySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file, uploadCaption);
    }
    // Reset input
    if (galleryInputRef.current) {
      galleryInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!window.confirm('Naozaj chcete vymazať túto fotku?')) {
      return;
    }

    isUpdatingRef.current = true; // Prevent useEffect from overwriting our changes

    try {
      console.log('🗑️ [PolePhotosTab] Deleting photo:', photoId);
      await polesAPI.deletePhoto(projectId, pole.id, photoId);

      // Update local state
      const updatedPhotos = photos.filter(p => p.id !== photoId);
      console.log('📸 [PolePhotosTab] Updating local state after delete:', {
        oldCount: photos.length,
        newCount: updatedPhotos.length,
        photoIds: updatedPhotos.map(p => p.id)
      });
      setPhotos(updatedPhotos);

      // Update parent
      console.log('⬆️ [PolePhotosTab] Calling onUpdate after delete...');
      onUpdate({ ...pole, photos: updatedPhotos });

      alert('✅ Fotka bola vymazaná!');

      // Wait a bit before allowing useEffect to update again
      setTimeout(() => {
        isUpdatingRef.current = false;
        console.log('🔓 [PolePhotosTab] Delete complete, allowing useEffect updates');
      }, 500);
    } catch (error: any) {
      console.error('❌ [PolePhotosTab] Error deleting photo:', error);
      alert(`❌ Chyba pri mazaní fotky: ${error.response?.data?.detail || error.message}`);
      isUpdatingRef.current = false; // Reset on error
    }
  };

  const handleSetPrimary = async (photoId: string) => {
    isUpdatingRef.current = true; // Prevent useEffect from overwriting our changes

    try {
      console.log('⭐ [PolePhotosTab] Setting primary photo:', photoId);
      await polesAPI.setPrimaryPhoto(projectId, pole.id, photoId);

      // Update local state
      const updatedPhotos = photos.map(p => ({
        ...p,
        is_primary: p.id === photoId
      }));
      setPhotos(updatedPhotos);

      // Update parent
      onUpdate({ ...pole, photos: updatedPhotos });

      alert('✅ Hlavná fotka bola nastavená!');

      setTimeout(() => {
        isUpdatingRef.current = false;
        console.log('🔓 [PolePhotosTab] Set primary complete, allowing useEffect updates');
      }, 500);
    } catch (error: any) {
      console.error('❌ [PolePhotosTab] Error setting primary photo:', error);
      alert(`❌ Chyba pri nastavovaní hlavnej fotky: ${error.response?.data?.detail || error.message}`);
      isUpdatingRef.current = false; // Reset on error
    }
  };

  const handleUpdateCaption = async (photoId: string, newCaption: string) => {
    isUpdatingRef.current = true; // Prevent useEffect from overwriting our changes

    try {
      console.log('✏️ [PolePhotosTab] Updating caption for photo:', photoId);
      await polesAPI.updatePhotoCaption(projectId, pole.id, photoId, newCaption);

      // Update local state
      const updatedPhotos = photos.map(p =>
        p.id === photoId ? { ...p, caption: newCaption } : p
      );
      setPhotos(updatedPhotos);

      // Update parent
      onUpdate({ ...pole, photos: updatedPhotos });

      setEditingCaption(null);
      setCaptionText('');

      setTimeout(() => {
        isUpdatingRef.current = false;
        console.log('🔓 [PolePhotosTab] Update caption complete, allowing useEffect updates');
      }, 500);
    } catch (error: any) {
      console.error('❌ [PolePhotosTab] Error updating caption:', error);
      alert(`❌ Chyba pri aktualizácii popisu: ${error.response?.data?.detail || error.message}`);
      isUpdatingRef.current = false; // Reset on error
    }
  };

  const startEditCaption = (photo: PolePhoto) => {
    setEditingCaption(photo.id);
    setCaptionText(photo.caption || '');
  };

  const cancelEditCaption = () => {
    setEditingCaption(null);
    setCaptionText('');
  };

  const primaryPhoto = photos.find(p => p.is_primary);

  return (
    <div className="pole-photos-tab">
      <div className="photos-header">
        <h3>📸 Fotodokumentácia stĺpika</h3>
        <div className="photos-count">
          {photos.length} / 10 fotiek
        </div>
      </div>

      {/* Upload Section */}
      <div className="upload-section">
        <div className="upload-caption-input">
          <input
            type="text"
            placeholder="Popis fotky (voliteľné)"
            value={uploadCaption}
            onChange={(e) => setUploadCaption(e.target.value)}
            disabled={uploading || photos.length >= 10}
          />
        </div>

        <div className="upload-buttons">
          {/* Camera Button */}
          <button
            className="upload-btn camera-btn"
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading || photos.length >= 10}
          >
            📸 Otvoriť kameru
          </button>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
            capture="environment"
            onChange={handleCameraCapture}
            style={{ display: 'none' }}
          />

          {/* Gallery Button */}
          <button
            className="upload-btn gallery-btn"
            onClick={() => galleryInputRef.current?.click()}
            disabled={uploading || photos.length >= 10}
          >
            🖼️ Vybrať z galérie
          </button>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
            onChange={handleGallerySelect}
            style={{ display: 'none' }}
          />
        </div>

        {uploading && <div className="uploading-indicator">⏳ Nahrávam...</div>}
      </div>

      {/* Photos Grid */}
      {photos.length === 0 ? (
        <div className="no-photos">
          <p>📷 Zatiaľ žiadne fotky</p>
          <p className="hint">Pridajte fotky pomocou tlačidiel vyššie</p>
        </div>
      ) : (
        <div className="photos-grid">
          {photos.map((photo) => (
            <div key={photo.id} className={`photo-card ${photo.is_primary ? 'primary' : ''}`}>
              {photo.is_primary && <div className="primary-badge">⭐ Hlavná</div>}
              
              <div className="photo-thumbnail">
                <img src={photo.thumbnail_url} alt={photo.caption || 'Fotka stĺpika'} />
              </div>

              <div className="photo-info">
                {editingCaption === photo.id ? (
                  <div className="caption-edit">
                    <input
                      type="text"
                      value={captionText}
                      onChange={(e) => setCaptionText(e.target.value)}
                      placeholder="Popis fotky"
                      autoFocus
                    />
                    <div className="caption-edit-buttons">
                      <button onClick={() => handleUpdateCaption(photo.id, captionText)}>
                        ✓
                      </button>
                      <button onClick={cancelEditCaption}>✕</button>
                    </div>
                  </div>
                ) : (
                  <div className="caption-display" onClick={() => startEditCaption(photo)}>
                    <p className="caption-text">
                      {photo.caption || <em>Kliknite pre pridanie popisu</em>}
                    </p>
                    <span className="edit-icon">✏️</span>
                  </div>
                )}

                <div className="photo-meta">
                  <small>
                    {new Date(photo.uploaded_at).toLocaleString('sk-SK')}
                  </small>
                  <small>{(photo.file_size / 1024).toFixed(0)} KB</small>
                </div>
              </div>

              <div className="photo-actions">
                {!photo.is_primary && (
                  <button
                    className="action-btn primary-btn"
                    onClick={() => handleSetPrimary(photo.id)}
                    title="Nastaviť ako hlavnú"
                  >
                    ⭐ Hlavná
                  </button>
                )}
                <a
                  href={photo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-btn view-btn"
                >
                  👁️ Zobraziť
                </a>
                <button
                  className="action-btn delete-btn"
                  onClick={() => handleDeletePhoto(photo.id)}
                  title="Vymazať"
                >
                  🗑️ Vymazať
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

